import React from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { FileText, CheckCircle, AlertTriangle, Scale, Globe, Mail, Calendar, Shield } from 'lucide-react';
import { SEO } from '../components/seo/SEO';
import { JsonLd } from '../components/seo/JsonLd';
import { createBreadcrumbSchema } from '../lib/structured-data';

export function TermsOfService() {
  return (
    <div className="min-h-screen flex flex-col bg-[var(--color-csai-bg)]">
      <Header />

      <main className="flex-grow">
        <SEO
          title="Terms of Service"
          description="Situs Canada terms of service. Usage agreements, data handling policies, and service terms for Canadian professionals."
          canonical="https://situsai.ca/terms"
        />
        <JsonLd data={createBreadcrumbSchema('/terms')} />

        {/* Hero Section */}
        <section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--color-csai-surface)] text-[var(--color-csai-primary)] text-sm font-medium mb-8">
            <FileText className="w-4 h-4" /> Legal Agreement
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-[var(--color-csai-accent)] tracking-tight">
            Terms of Service for<br className="hidden sm:block" /> Situs AI and Orca AI
          </h1>
          <p className="text-xl text-[var(--color-csai-muted)] max-w-3xl mx-auto mb-10">
            Please read these terms carefully before using Orca AI. By accessing or using our service, you agree to be bound by these terms.
          </p>
          <p className="text-sm text-[var(--color-csai-muted)]">
            <strong>Effective Date:</strong> March 19, 2026
          </p>
        </section>

        {/* Table of Contents */}
        <section className="py-8 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl border border-[var(--color-csai-surface)] p-6 mb-8">
            <h2 className="text-lg font-semibold text-[var(--color-csai-accent)] mb-4">Table of Contents</h2>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-[var(--color-csai-muted)]">
              <li><a href="#acceptance" className="hover:text-[var(--color-csai-primary)]">1. Acceptance of Terms</a></li>
              <li><a href="#description" className="hover:text-[var(--color-csai-primary)]">2. Description of Service</a></li>
              <li><a href="#accounts" className="hover:text-[var(--color-csai-primary)]">3. User Accounts</a></li>
              <li><a href="#acceptable-use" className="hover:text-[var(--color-csai-primary)]">4. Acceptable Use</a></li>
              <li><a href="#intellectual-property" className="hover:text-[var(--color-csai-primary)]">5. Intellectual Property</a></li>
              <li><a href="#data-handling" className="hover:text-[var(--color-csai-primary)]">6. Data Handling</a></li>
              <li><a href="#disclaimers" className="hover:text-[var(--color-csai-primary)]">7. Disclaimers</a></li>
              <li><a href="#liability" className="hover:text-[var(--color-csai-primary)]">8. Limitation of Liability</a></li>
              <li><a href="#governing-law" className="hover:text-[var(--color-csai-primary)]">9. Governing Law</a></li>
              <li><a href="#disputes" className="hover:text-[var(--color-csai-primary)]">10. Dispute Resolution</a></li>
              <li><a href="#changes" className="hover:text-[var(--color-csai-primary)]">11. Changes to Terms</a></li>
              <li><a href="#contact" className="hover:text-[var(--color-csai-primary)]">12. Contact</a></li>
            </ul>
          </div>
        </section>

        {/* Content Sections */}
        <section className="py-8 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto space-y-8">
          {/* 1. Acceptance */}
          <div id="acceptance" className="bg-white rounded-2xl border border-[var(--color-csai-surface)] p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[var(--color-csai-surface)] text-[var(--color-csai-primary)] rounded-xl flex items-center justify-center">
                <CheckCircle className="w-5 h-5" />
              </div>
              <h2 className="text-2xl font-bold text-[var(--color-csai-accent)]">1. Acceptance of Terms</h2>
            </div>
            <p className="text-[var(--color-csai-muted)] leading-relaxed">
              By creating an account or using Orca AI, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service and our Privacy Policy. If you are using Orca AI on behalf of an organization, you represent that you have the authority to bind that organization to these terms.
            </p>
            <p className="text-[var(--color-csai-muted)] leading-relaxed mt-4">
              If you do not agree to these terms, you may not access or use our service.
            </p>
          </div>

          {/* 2. Description of Service */}
          <div id="description" className="bg-white rounded-2xl border border-[var(--color-csai-surface)] p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[var(--color-csai-surface)] text-[var(--color-csai-primary)] rounded-xl flex items-center justify-center">
                <Shield className="w-5 h-5" />
              </div>
              <h2 className="text-2xl font-bold text-[var(--color-csai-accent)]">2. Description of Service</h2>
            </div>
            <p className="text-[var(--color-csai-muted)] leading-relaxed mb-4">
              Orca AI is a Canadian AI assistant designed exclusively for professional practices, providing:
            </p>
            <ul className="space-y-3 text-[var(--color-csai-muted)]">
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[var(--color-csai-primary)] rounded-full mt-2 shrink-0"></span>
                <span>AI-powered responses to professional queries in accounting, legal, and financial domains</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[var(--color-csai-primary)] rounded-full mt-2 shrink-0"></span>
                <span>Document analysis and extraction capabilities</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[var(--color-csai-primary)] rounded-full mt-2 shrink-0"></span>
                <span>Research assistance and scenario analysis</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[var(--color-csai-primary)] rounded-full mt-2 shrink-0"></span>
                <span>Compliance and regulatory reference support</span>
              </li>
            </ul>
            <p className="text-[var(--color-csai-muted)] leading-relaxed mt-4">
              We reserve the right to modify, suspend, or discontinue any aspect of Orca AI at any time, with reasonable notice where practicable.
            </p>
          </div>

          {/* 3. User Accounts */}
          <div id="accounts" className="bg-white rounded-2xl border border-[var(--color-csai-surface)] p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[var(--color-csai-surface)] text-[var(--color-csai-primary)] rounded-xl flex items-center justify-center">
                <CheckCircle className="w-5 h-5" />
              </div>
              <h2 className="text-2xl font-bold text-[var(--color-csai-accent)]">3. User Accounts</h2>
            </div>
            <p className="text-[var(--color-csai-muted)] leading-relaxed mb-4">
              To access Orca AI, you must create an account. You are responsible for:
            </p>
            <ul className="space-y-3 text-[var(--color-csai-muted)]">
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 shrink-0"></span>
                <span>Maintaining the confidentiality of your login credentials</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 shrink-0"></span>
                <span>All activities that occur under your account</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 shrink-0"></span>
                <span>Notifying us immediately of any unauthorized use</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 shrink-0"></span>
                <span>Providing accurate and complete information during registration</span>
              </li>
            </ul>
            <p className="text-[var(--color-csai-muted)] leading-relaxed mt-4">
              We reserve the right to suspend or terminate accounts that violate these terms or engage in fraudulent activity.
            </p>
          </div>

          {/* 4. Acceptable Use */}
          <div id="acceptable-use" className="bg-white rounded-2xl border border-[var(--color-csai-surface)] p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[var(--color-csai-surface)] text-[var(--color-csai-primary)] rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <h2 className="text-2xl font-bold text-[var(--color-csai-accent)]">4. Acceptable Use</h2>
            </div>
            <p className="text-[var(--color-csai-muted)] leading-relaxed mb-4">
              You agree NOT to use Orca AI to:
            </p>
            <ul className="space-y-3 text-[var(--color-csai-muted)]">
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-red-500 rounded-full mt-2 shrink-0"></span>
                <span>Generate content that is illegal, harmful, or fraudulent</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-red-500 rounded-full mt-2 shrink-0"></span>
                <span>Impersonate any person or entity</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-red-500 rounded-full mt-2 shrink-0"></span>
                <span>Attempt to extract, scrape, or reverse engineer our AI models or training data</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-red-500 rounded-full mt-2 shrink-0"></span>
                <span>Use the service to develop competing AI products or services</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-red-500 rounded-full mt-2 shrink-0"></span>
                <span>Upload malicious code, viruses, or harmful content</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-red-500 rounded-full mt-2 shrink-0"></span>
                <span>Violate any applicable federal, provincial, or municipal laws</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-red-500 rounded-full mt-2 shrink-0"></span>
                <span>Attempt to circumvent content safety measures or usage limits</span>
              </li>
            </ul>
          </div>

          {/* 5. Intellectual Property */}
          <div id="intellectual-property" className="bg-white rounded-2xl border border-[var(--color-csai-surface)] p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[var(--color-csai-surface)] text-[var(--color-csai-primary)] rounded-xl flex items-center justify-center">
                <FileText className="w-5 h-5" />
              </div>
              <h2 className="text-2xl font-bold text-[var(--color-csai-accent)]">5. Intellectual Property</h2>
            </div>
            <p className="text-[var(--color-csai-muted)] leading-relaxed mb-4">
              <strong>Our Property:</strong> Situs AI and Orca AI, including all underlying AI models, software, algorithms, and the Orca AI brand, are the exclusive property of Situs AI Inc. These Terms of Service do not grant you any ownership rights.
            </p>
            <p className="text-[var(--color-csai-muted)] leading-relaxed mb-4">
              <strong>Your Property:</strong> You retain all rights to the data, documents, and content you upload to Orca AI. You grant us a limited license to process your data solely for the purpose of providing the service.
            </p>
            <p className="text-[var(--color-csai-muted)] leading-relaxed">
              <strong>AI-Generated Content:</strong> Responses generated by Orca AI may be used by you for your professional purposes. We do not claim ownership of the output, but you are responsible for verifying its accuracy and suitability for your use case.
            </p>
          </div>

          {/* 6. Data Handling */}
          <div id="data-handling" className="bg-white rounded-2xl border border-[var(--color-csai-surface)] p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[var(--color-csai-surface)] text-[var(--color-csai-primary)] rounded-xl flex items-center justify-center">
                <Shield className="w-5 h-5" />
              </div>
              <h2 className="text-2xl font-bold text-[var(--color-csai-accent)]">6. Data Handling</h2>
            </div>
            <p className="text-[var(--color-csai-muted)] leading-relaxed mb-4">
              Your trust is paramount. We handle your data as follows:
            </p>
            <ul className="space-y-3 text-[var(--color-csai-muted)]">
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 shrink-0"></span>
                <span><strong>Canadian Storage:</strong> All data is stored exclusively in Montreal, Quebec data centers</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 shrink-0"></span>
                <span><strong>Zero Training:</strong> Your data is never used to train, fine-tune, or improve our AI models</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 shrink-0"></span>
                <span><strong>Isolation:</strong> Your data is stored in isolated vaults, accessible only to you</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 shrink-0"></span>
                <span><strong>Encryption:</strong> Data is encrypted at rest and in transit</span>
              </li>
            </ul>
            <p className="text-[var(--color-csai-muted)] text-sm mt-4">
              For full details, see our <Link to="/privacy" className="text-[var(--color-csai-primary)] hover:underline">Privacy Policy</Link>.
            </p>
          </div>

          {/* 7. Disclaimers */}
          <div id="disclaimers" className="bg-white rounded-2xl border border-[var(--color-csai-surface)] p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[var(--color-csai-surface)] text-[var(--color-csai-primary)] rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <h2 className="text-2xl font-bold text-[var(--color-csai-accent)]">7. Disclaimers</h2>
            </div>
            <p className="text-[var(--color-csai-muted)] leading-relaxed mb-4">
              ORCA AI IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.
            </p>
            <ul className="space-y-3 text-[var(--color-csai-muted)]">
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-red-500 rounded-full mt-2 shrink-0"></span>
                <span>We do not warrant that the service will be uninterrupted, secure, or error-free</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-red-500 rounded-full mt-2 shrink-0"></span>
                <span>We do not warrant that the output will be accurate, complete, or suitable for your specific needs</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-red-500 rounded-full mt-2 shrink-0"></span>
                <span>AI-generated content should not be used as a substitute for professional advice in accounting, legal, or financial matters</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-red-500 rounded-full mt-2 shrink-0"></span>
                <span>You are responsible for verifying all AI outputs before relying on them</span>
              </li>
            </ul>
          </div>

          {/* 8. Limitation of Liability */}
          <div id="liability" className="bg-white rounded-2xl border border-[var(--color-csai-surface)] p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[var(--color-csai-surface)] text-[var(--color-csai-primary)] rounded-xl flex items-center justify-center">
                <Scale className="w-5 h-5" />
              </div>
              <h2 className="text-2xl font-bold text-[var(--color-csai-accent)]">8. Limitation of Liability</h2>
            </div>
            <p className="text-[var(--color-csai-muted)] leading-relaxed mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, SITUS AI INC. SHALL NOT BE LIABLE FOR:
            </p>
            <ul className="space-y-3 text-[var(--color-csai-muted)]">
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[var(--color-csai-primary)] rounded-full mt-2 shrink-0"></span>
                <span>Any indirect, incidental, special, consequential, or punitive damages</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[var(--color-csai-primary)] rounded-full mt-2 shrink-0"></span>
                <span>Loss of profits, revenue, data, or business opportunities</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[var(--color-csai-primary)] rounded-full mt-2 shrink-0"></span>
                <span>Damages arising from reliance on AI-generated content</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[var(--color-csai-primary)] rounded-full mt-2 shrink-0"></span>
                <span>Any damages exceeding the total fees paid by you to Situs AI in the twelve (12) months preceding the claim</span>
              </li>
            </ul>
          </div>

          {/* 9. Governing Law */}
          <div id="governing-law" className="bg-white rounded-2xl border border-[var(--color-csai-surface)] p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[var(--color-csai-surface)] text-[var(--color-csai-primary)] rounded-xl flex items-center justify-center">
                <Globe className="w-5 h-5" />
              </div>
              <h2 className="text-2xl font-bold text-[var(--color-csai-accent)]">9. Governing Law</h2>
            </div>
            <p className="text-[var(--color-csai-muted)] leading-relaxed">
              These Terms of Service shall be governed by and construed in accordance with the laws of the Province of Quebec and the federal laws of Canada applicable therein, without regard to conflict of law principles.
            </p>
            <p className="text-[var(--color-csai-muted)] leading-relaxed mt-4">
              Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts of the Province of Quebec.
            </p>
          </div>

          {/* 10. Dispute Resolution */}
          <div id="disputes" className="bg-white rounded-2xl border border-[var(--color-csai-surface)] p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[var(--color-csai-surface)] text-[var(--color-csai-primary)] rounded-xl flex items-center justify-center">
                <Scale className="w-5 h-5" />
              </div>
              <h2 className="text-2xl font-bold text-[var(--color-csai-accent)]">10. Dispute Resolution</h2>
            </div>
            <p className="text-[var(--color-csai-muted)] leading-relaxed mb-4">
              Before initiating formal proceedings, you agree to attempt to resolve disputes informally:
            </p>
            <ul className="space-y-3 text-[var(--color-csai-muted)]">
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 shrink-0"></span>
                <span><strong>Step 1 - Notice:</strong> Contact us at <a href="mailto:info@situsai.ca" className="text-[var(--color-csai-primary)] hover:underline">info@situsai.ca</a> with your concern</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 shrink-0"></span>
                <span><strong>Step 2 - Good Faith Discussion:</strong> We will endeavor to resolve the issue within 30 days</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 shrink-0"></span>
                <span><strong>Step 3 - Formal Proceedings:</strong> If unresolved, disputes proceed to the courts of Quebec</span>
              </li>
            </ul>
          </div>

          {/* 11. Changes to Terms */}
          <div id="changes" className="bg-white rounded-2xl border border-[var(--color-csai-surface)] p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[var(--color-csai-surface)] text-[var(--color-csai-primary)] rounded-xl flex items-center justify-center">
                <Calendar className="w-5 h-5" />
              </div>
              <h2 className="text-2xl font-bold text-[var(--color-csai-accent)]">11. Changes to Terms</h2>
            </div>
            <p className="text-[var(--color-csai-muted)] leading-relaxed mb-4">
              We may update these Terms of Service from time to time to reflect changes in our service, legal requirements, or business practices.
            </p>
            <ul className="space-y-3 text-[var(--color-csai-muted)]">
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[var(--color-csai-primary)] rounded-full mt-2 shrink-0"></span>
                <span>We will notify you of material changes via email at least 30 days before they take effect</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[var(--color-csai-primary)] rounded-full mt-2 shrink-0"></span>
                <span>Continued use of the service after changes constitutes acceptance of the new terms</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-[var(--color-csai-primary)] rounded-full mt-2 shrink-0"></span>
                <span>Minor changes (typo fixes, clarifications) may be made without notice</span>
              </li>
            </ul>
          </div>

          {/* 12. Contact */}
          <div id="contact" className="bg-white rounded-2xl border border-[var(--color-csai-surface)] p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[var(--color-csai-surface)] text-[var(--color-csai-primary)] rounded-xl flex items-center justify-center">
                <Mail className="w-5 h-5" />
              </div>
              <h2 className="text-2xl font-bold text-[var(--color-csai-accent)]">12. Contact Us</h2>
            </div>
            <p className="text-[var(--color-csai-muted)] leading-relaxed mb-4">
              If you have any questions about these Terms of Service, please contact our legal team:
            </p>
            <div className="bg-[var(--color-csai-surface)] rounded-xl p-6 space-y-2 text-[var(--color-csai-muted)]">
              <p className="font-semibold text-[var(--color-csai-accent)]">Situs AI Inc.</p>
              <p>Legal Department</p>
              <p>Montreal, Quebec, Canada</p>
              <p>Email: <a href="mailto:info@situsai.ca" className="text-[var(--color-csai-primary)] hover:underline">info@situsai.ca</a></p>
            </div>
          </div>
        </section>

        {/* Back to Home */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto text-center">
          <Link to="/">
            <Button variant="secondary" className="text-lg px-8">Back to Home</Button>
          </Link>
        </section>
      </main>

      <Footer />
    </div>
  );
}
